import { Injectable, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

import { ConfigService } from '@mimecast-ui/universal-login/data-access-config';

/** Cookie name shared with consuming apps (SessionService.initialize). */
export const MC_SG_COOKIE = 'mc-sg';

/** Matches legacy MC_SG_COOKIE_EXPIRY_TIME in session-handler-service.js. */
const MC_SG_COOKIE_EXPIRY_MS = 12 * 60 * 60 * 1000;

const PENDO_URL = 'https://app.pendo.io/';

const OFFICE_ADDIN_APP_KEYS = ['mimecast-moa', 'mimecast-moa-sm'] as const;
const SAFE_SEND_APP_KEY = 'mimecast-ssa';

export type SessionCookieSameSite = 'Strict' | 'None';

export interface LoginSessionPayload {
  sessionId?: string;
  remoteAccountCode?: string;
}

interface McSgCookieValue {
  id: string;
  remoteAccountCode?: string;
  origin?: 'pendo';
}

/**
 * Writes the client-side mc-sg session cookie after successful authentication,
 * using the same SameSite matrix as legacy sessionHandlerService.createCookie.
 * Context helpers are shared with the x-mc-strict header work (UIP-19432).
 */
@Injectable({ providedIn: 'root' })
export class LoginSessionService {
  private readonly cookieService = inject(CookieService);
  private readonly configService = inject(ConfigService);

  setSession(sessionResponse: LoginSessionPayload): void {
    if (!sessionResponse.sessionId) {
      return;
    }

    const session: McSgCookieValue = {
      id: sessionResponse.sessionId,
      remoteAccountCode: sessionResponse.remoteAccountCode,
    };

    if (this.isAppRunningInPendo()) {
      session.origin = 'pendo';
    }

    this.writeCookie(session);
  }

  /**
   * Parses mc-sg JSON. Returns session payload when id is non-empty; otherwise null.
   * Must not delete or rewrite the cookie.
   */
  readSessionFromCookie(): LoginSessionPayload | null {
    const raw = this.cookieService.get(MC_SG_COOKIE);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as McSgCookieValue;
      if (!parsed?.id) {
        return null;
      }

      return {
        sessionId: parsed.id,
        remoteAccountCode: parsed.remoteAccountCode,
      };
    } catch {
      return null;
    }
  }

  hasActiveSessionCookie(): boolean {
    return !!this.readSessionFromCookie()?.sessionId;
  }

  isAppRunningInPendo(): boolean {
    try {
      return (
        !!document.referrer &&
        document.referrer.startsWith(PENDO_URL) &&
        this.inIframe()
      );
    } catch {
      return false;
    }
  }

  isOfficeAddIn(): boolean {
    const currentAppId = this.configService.resolveAppConfig().xMcAppId;
    if (!currentAppId) {
      return false;
    }

    const { appsConfig } = this.configService;
    return OFFICE_ADDIN_APP_KEYS.some(
      (key) => appsConfig[key]?.['x-mc-app-id'] === currentAppId
    );
  }

  isSafeSend(): boolean {
    const currentAppId = this.configService.resolveAppConfig().xMcAppId;
    if (!currentAppId) {
      return false;
    }

    return (
      this.configService.appsConfig[SAFE_SEND_APP_KEY]?.['x-mc-app-id'] ===
      currentAppId
    );
  }

  /**
   * SameSite for the mc-sg cookie. Legacy omits the attribute for Pendo-only
   * (browsers treat that as Lax); return undefined to omit it here too.
   */
  resolveSessionCookieSameSite(): SessionCookieSameSite | undefined {
    if (
      !this.isAppRunningInPendo() &&
      !this.isOfficeAddIn() &&
      !this.isSafeSend()
    ) {
      return 'Strict';
    }

    if (this.isOfficeAddIn() || this.isSafeSend()) {
      return 'None';
    }

    return undefined;
  }

  /**
   * SameSite for mc-locale. Legacy createLocaleCookie only checks Pendo + Office
   * (not SafeSend). Keep that two-context matrix; do not reuse SafeSend branch.
   */
  resolveLocaleCookieSameSite(): SessionCookieSameSite | undefined {
    if (!this.isAppRunningInPendo() && !this.isOfficeAddIn()) {
      return 'Strict';
    }
    if (this.isOfficeAddIn()) {
      return 'None';
    }
    return undefined;
  }

  private writeCookie(session: McSgCookieValue): void {
    const expires = new Date(Date.now() + MC_SG_COOKIE_EXPIRY_MS);
    const sameSite = this.resolveSessionCookieSameSite();

    this.cookieService.set(
      MC_SG_COOKIE,
      JSON.stringify(session),
      sameSite === undefined
        ? {
            expires,
            path: '/',
            secure: true,
          }
        : {
            expires,
            path: '/',
            secure: true,
            sameSite,
          }
    );
  }

  private inIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }
}
